import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Flex, useToast, VStack, Text, Card, Stat, StatLabel, StatNumber, Heading, HStack, Center, Spinner } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getAdminDashboard } from './admin-service';
import { AdminDashboardResult } from './admin-interfaces';
import { useTranslation } from 'react-i18next';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const AdminPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const initialState: AdminDashboardResult = {
    counts: {
      partners: 0,
      analysts: 0,
    },
    credits: {
      userCredits: 0,
      totalCreditsSent: 0,
      chartData: [],
    },
    assessments: {
      all: '0',
      waiting: '0',
      done: '0',
    },
    invoices: {
      status: {
        paid: 0,
        pending: 0,
        cancelled: 0
      },
      metrics: {
        averageOrderValue: 0,
        totalDiscounts: 0,
        totalSales: 0
      },
      monthly: {
        months: [],
        invoiceAmounts: [],
        invoiceCounts: []
      }
    }
  };

  const [data, setData] = useState<AdminDashboardResult>(initialState);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAdminDashboard();
        setData(response.message);
      } catch (err) {
        toast({
          title: t('general.error'),
          description: t('general.error_loading_data'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        toast.closeAll()
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Prepare invoice chart data
  const invoiceChartData = data.invoices.monthly?.months?.map((month: string, index: number) => ({
    month,
    amount: data.invoices.monthly?.invoiceAmounts?.[index] || 0,
    count: data.invoices.monthly?.invoiceCounts?.[index] || 0
  })) || [];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <HStack justify="space-between" mb={4} px={4} pt={4} w="100%">
        <VStack align="start">
          <Heading>{t('admin.dashboard_title')}</Heading>
        </VStack>
      </HStack>
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        as={VStack}
        gap='20px'
        align="stretch"
      >
        {/* Main Chart and Statistics */}
        <Flex direction={{ base: 'column', lg: 'row' }} gap={'20px'}>
          <MotionCard
            variants={cardVariants}
            p={6}
            flex="1"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="none"
            transition="border-color 0.2s"
          >
            <Text fontSize="lg" fontWeight="bold" mb={4}>{t('admin.dashboard_title')}</Text>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={invoiceChartData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4299e1" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4299e1" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#805AD5" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#805AD5" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    if (name === 'amount') {
                      return [formatCurrency(value), t('general.amount')];
                    }
                    return [value, t('general.count')];
                  }}
                  labelFormatter={(label) => `${t('general.months')}: ${label}`}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="amount"
                  stroke="#4299e1"
                  fillOpacity={1}
                  fill="url(#colorAmount)"
                  name="amount"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="count"
                  stroke="#805AD5"
                  fillOpacity={1}
                  fill="url(#colorCount)"
                  name="count"
                />
              </AreaChart>
            </ResponsiveContainer>
          </MotionCard>

          <Box w={{ base: '100%', lg: '30%' }}>
            <VStack gap='20px' mb={'20px'}>
              <MotionCard
                variants={cardVariants}
                p={6}
                w="100%"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="none"
                transition="border-color 0.2s"
              >
                <Stat>
                  <StatLabel>{t('admin.dashboard_indicator_partners')}</StatLabel>
                  <StatNumber>{data.counts.partners}</StatNumber>
                </Stat>
              </MotionCard>

              <MotionCard
                variants={cardVariants}
                p={6}
                w="100%"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="none"
                transition="border-color 0.2s"
              >
                <Stat>
                  <StatLabel>{t('admin.dashboard_indicator_analysts')}</StatLabel>
                  <StatNumber>{data.counts.analysts}</StatNumber>
                </Stat>
              </MotionCard>

              <MotionCard
                variants={cardVariants}
                p={6}
                w="100%"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="none"
                transition="border-color 0.2s"
              >
                <Stat>
                  <StatLabel>{t('admin.dashboard_indicator_credits_available')}</StatLabel>
                  <StatNumber>{data.credits.userCredits}</StatNumber>
                </Stat>
              </MotionCard>

              <MotionCard
                variants={cardVariants}
                p={6}
                w="100%"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="none"
                transition="border-color 0.2s"
              >
                <Stat>
                  <StatLabel>{t('admin.dashboard_indicator_assessments_sent')}</StatLabel>
                  <StatNumber>{data.assessments.all}</StatNumber>
                </Stat>
              </MotionCard>

              <MotionCard
                variants={cardVariants}
                p={6}
                w="100%"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="none"
                transition="border-color 0.2s"
              >
                <Stat>
                  <StatLabel>{t('admin.dashboard_indicator_assessments_answered')}</StatLabel>
                  <StatNumber>{data.assessments.done}</StatNumber>
                </Stat>
              </MotionCard>
            </VStack>
          </Box>
        </Flex>
      </MotionBox>
    </>
  );
};

export default AdminPage; 